import React, { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch, type Control, type FieldValues } from 'react-hook-form';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import { CampaignStatuses } from 'src/store/constants';
import { type BulkEditMode, ConfigurationType, CreationSource, DEFAULT_REFRESH_SCHEDULE_CRON } from 'src/constants';
import { useCampaignConfigurationControllerFindManyQuery, useCampaignsControllerUpdateMutation, useProgramsControllerUpdateMutation } from 'src/store/leela-api';
import { type CampaignConfigurationDto, type CampaignDesiredStateDto, type Schedule } from 'src/store/generated/leela-api-generated';
import { AlertType } from 'src/components/common/alerts/alert.types';
import { useToasterContext } from 'src/components/common/alerts/ToasterContext';
import { FormSelect } from 'src/components/common/forms/FormSelect';
import { FormSharedUserInput } from 'src/components/common/forms/FormSharedUserInput';
import { FormSchedule } from 'src/components/common/forms/FormSchedule';
import { useDebounce } from 'src/hooks/useDebounce';

export type { BulkEditMode };

interface BulkEditModalProps {
  mode: BulkEditMode;
  campaigns: CampaignDesiredStateDto[];
  onClose: () => void;
  onSuccess: () => void;
}

interface BulkEditFormData {
  configIds: string[];
  userSharedList: string[];
  status: CampaignDesiredStateDto['status'];
  schedule: Schedule;
}

export const BulkEditModal: React.FC<BulkEditModalProps> = ({
  mode,
  campaigns,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { createToast } = useToasterContext();
  const [updateCampaign] = useCampaignsControllerUpdateMutation();
  const [updateProgram] = useProgramsControllerUpdateMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [blocklistSearch, setBlocklistSearch] = useState('');
  const [debouncedBlocklistSearch] = useDebounce(blocklistSearch, 300) as [string, boolean];

  const { data: blocklistConfigs, isFetching: isBlocklistFetching } = useCampaignConfigurationControllerFindManyQuery(
    {
      configType: ConfigurationType.GTI_BLOCKLIST,
      source: CreationSource.USER,
      includeCampaignCount: false,
      pageSize: 20,
      page: 1,
      sort: ['updatedAt:-1'],
      ...(debouncedBlocklistSearch && { search: debouncedBlocklistSearch }),
    },
    { skip: mode !== 'blocklist' },
  );

  const [selectedBlocklists, setSelectedBlocklists] = useState<Array<{ label: string; value: string }>>([]);

  const blocklistOptions = useMemo(() => {
    const searchResults = (blocklistConfigs?.data ?? []).map((config: CampaignConfigurationDto) => ({
      label: config.name ?? config.id,
      value: config.id,
    }));
    const resultIds = new Set(searchResults.map(o => o.value));
    const missingSelected = selectedBlocklists.filter(o => !resultIds.has(o.value));
    return [...searchResults, ...missingSelected];
  }, [blocklistConfigs, selectedBlocklists]);

  const { control, handleSubmit } = useForm<BulkEditFormData>({
    defaultValues: {
      configIds: [],
      userSharedList: [],
      status: CampaignStatuses.ACTIVE as CampaignDesiredStateDto['status'],
      schedule: { refreshScheduleCron: DEFAULT_REFRESH_SCHEDULE_CRON },
    },
  });

  const formControl = control as unknown as Control<FieldValues>;

  // Track selected blocklists so they persist in options during search
  const watchedConfigIds = useWatch({ control, name: 'configIds' }) as string[];
  useEffect(() => {
    if (!watchedConfigIds?.length) return;
    const selected = blocklistOptions.filter(o => watchedConfigIds.includes(o.value));
    if (selected.length > 0) setSelectedBlocklists(selected);
  }, [watchedConfigIds, blocklistOptions]);

  const titleMap: Record<BulkEditMode, string> = {
    share: t('bulk_edit_share_title'),
    blocklist: t('bulk_edit_blocklist_title'),
    status: t('bulk_edit_status_title'),
    schedule: t('bulk_edit_schedule_title'),
  };
  const title = titleMap[mode];

  const onSubmit = handleSubmit(async (data) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    const failedIds: string[] = [];

    try {
      if (mode === 'blocklist' || mode === 'status') {
        for (const campaign of campaigns) {
          const dto = mode === 'blocklist'
            ? { programId: campaign.programId, publisher: campaign.publisher, configIds: data.configIds, priority: 'IMMEDIATE' as const }
            : { programId: campaign.programId, publisher: campaign.publisher, status: data.status, priority: 'IMMEDIATE' as const };
          const response = await updateCampaign({
            id: campaign.campaignId,
            updateCampaignDto: dto,
          });
          if ('error' in response) {
            failedIds.push(campaign.campaignId);
          }
        }
      } else {
        const uniqueProgramIds = Array.from(new Set(campaigns.map((c) => c.programId)));
        const programUpdateDto = mode === 'schedule'
          ? { schedule: data.schedule }
          : { userSharedList: data.userSharedList };
        for (const programId of uniqueProgramIds) {
          const response = await updateProgram({
            id: programId,
            updateProgramDto: programUpdateDto,
          });
          if ('error' in response) {
            campaigns.filter((c) => c.programId === programId).forEach((c) => failedIds.push(c.campaignId));
          }
        }
      }
    } finally {
      setIsSubmitting(false);
    }

    const failureKeyMap: Record<BulkEditMode, string> = {
      blocklist: 'bulk_edit_blocklist_failure',
      share: 'bulk_edit_program_failure',
      status: 'bulk_edit_status_failure',
      schedule: 'bulk_edit_schedule_failure',
    };

    if (failedIds.length === 0) {
      createToast({
        type: AlertType.SUCCESS,
        message: t('bulk_edit_success', { count: campaigns.length }),
      });
      onSuccess();
    } else {
      createToast({
        type: AlertType.ERROR,
        message: t(failureKeyMap[mode], { failedIds: failedIds.join(', ') }),
      });
    }
  });

  return (
    <Modal
      data-testid="bulk-edit-modal"
      open
      scrollContainer="viewport"
      title={title}
      width="600px"
      onClose={!isSubmitting ? onClose : undefined}
    >
      <Column spacing="400">
        <Text type="b300">
          {t('bulk_edit_count', { count: campaigns.length })}
        </Text>

        {mode === 'blocklist' && (
          <FormSelect
            control={formControl}
            data-testid="bulk-edit-blocklist-select"
            label={t('bulk_edit_apply_blocklist')}
            loading={isBlocklistFetching}
            name="configIds"
            options={blocklistOptions}
            searchQuery={blocklistSearch}
            onSearch={setBlocklistSearch}
          />
        )}

        {mode === 'share' && (
          <FormSharedUserInput
            control={formControl}
            name="userSharedList"
          />
        )}

        {mode === 'status' && (
          <FormSelect
            control={formControl}
            data-testid="bulk-edit-status-select"
            label={t('form_global_status')}
            name="status"
            options={Object.values(CampaignStatuses).map((status: CampaignDesiredStateDto['status']) => ({
              label: status,
              value: status,
            }))}
            required
          />
        )}

        {mode === 'schedule' && (
          <FormSchedule control={formControl} name="schedule" />
        )}
      </Column>

      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fit">
          {isSubmitting && (
            <Text data-testid="bulk-edit-progress" type="b300">
              {t('bulk_edit_progress')}
            </Text>
          )}
          <Button
            data-testid="bulk-edit-cancel"
            disabled={isSubmitting}
            type="secondary"
            onClick={onClose}
          >
            {t('buttons_cancel')}
          </Button>
          <Button
            data-testid="bulk-edit-apply"
            disabled={isSubmitting}
            type="primary"
            onClick={onSubmit}
          >
            {t('bulk_edit_apply')}
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};
